import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { HttpBodyResponse } from 'src/app/core/api-dto/interface/api-http-response.interface';
import { Logger } from 'src/app/core/interfaces/Logger';
import { environment } from 'src/environments/environment';
import { BookingSpace } from '../../../domain/models/space.model';
import { IApiBookingSpace } from '../../interfaces/booking-space.interface';
import { BookingSpaceMapper } from '../../mappers/space.mapper';

@Injectable({ providedIn: 'root' })
export class BookingSpaceApiService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly baseUrl = `${environment.apiURL}/booking-spaces`;

  getAll(): Observable<BookingSpace[]> {
    return this.get<IApiBookingSpace[]>().pipe(
      map(apiSpaces => apiSpaces.map(apiSpace => this.toBookingSpace(apiSpace)))
    );
  }

  update(space: BookingSpace): Observable<boolean> {
    const payload = BookingSpaceMapper.toJSON(space);

    return this.put<IApiBookingSpace>(space?.id, payload).pipe(
      map(() => true)
    );
  }

  private get<T>(endpoint = ''): Observable<T> {
    return this.makeRequest<T>(
      this.http.get<HttpBodyResponse<T>>(`${this.baseUrl}/${endpoint}`.replace(/\/$/, ''))
    );
  }

  private put<T>(id: string, payload: unknown): Observable<T> {
    return this.makeRequest<T>(
      this.http.put<HttpBodyResponse<T>>(`${this.baseUrl}/${id}`, payload)
    );
  }

  private makeRequest<T>(request: Observable<HttpBodyResponse<T>>): Observable<T> {
    return request.pipe(
      map(response => response.data!),
      catchError(error => this.handleError(error))
    );
  }

  private toBookingSpace = (apiSpace: IApiBookingSpace): BookingSpace => {
    return BookingSpaceMapper.fromJSON(apiSpace);
  };

  private handleError = (error: unknown): Observable<never> => {
    this.logger.error(
      new Error('[BookingSpaceApiService] Operation failed'),
      error
    );

    return throwError(() => error);
  };
}
